import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function ResetPassword() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/users/me/set-password", { password: pw });
      await refresh();
      window.sessionStorage.removeItem("gw_forgot_password");
      toast.success("Password updated");
      nav("/");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Toaster richColors position="top-right" />
      <Card className="w-full max-w-md p-8 border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-6 h-6 text-emerald-600" />
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Set a new password</h1>
            <p className="text-xs text-slate-500 mt-0.5">Verified as {user?.email}</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <Label>New password</Label>
            <div className="relative mt-1">
              <Input
                data-testid="reset-pw-input"
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="pr-10"
                placeholder="Min 6 characters"
                required
              />
              <button type="button" data-testid="reset-toggle" onClick={() => setShow(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input
              data-testid="reset-pw-confirm"
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1"
              placeholder="Re-enter"
              required
            />
          </div>
          <Button data-testid="reset-submit" type="submit" disabled={busy || !pw || !confirm} className="w-full bg-indigo-600 hover:bg-indigo-700">
            {busy ? "Saving…" : "Save new password"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
